// Consumer-side reader for the storefront's global styleConfig JSON string
// (IStorefrontSettings.styleConfig), which the admin Home editor writes. Today it holds
// page-level style (background/text/fonts); here we read the additive `spinner` sub-key that
// styles the storefront-wide loading spinner. Absent config = zero overrides (spinners render
// exactly as before), so this is strictly additive.

// The styling knobs for the app-wide loading spinner.
export interface ISpinnerConfig {
    // Color of the spinner ring itself (the Fluent spinner SVG stroke).
    color?: string;
    // Color of the label text shown beneath the spinner.
    labelColor?: string;
    // Font family of the label text.
    labelFont?: string;
    // Fluent spinner size preset ('tiny' | 'small' | 'medium' | 'large' | 'huge' | ...).
    size?: string;
}

// One layer of the animated main-body background. Layers stack in a fixed backdrop behind the
// page content; giving each layer its own animation speed produces a parallax / depth effect
// (e.g. a "flying through space" starfield built from several radial-gradient layers). Everything
// here is generic CSS — the specific effect (gradients, keyframes, speeds) lives in the config,
// never in the app code.
export interface IPageBackgroundLayer {
    // The layer's background-image (e.g. one or more comma-separated radial-gradient(...) stars).
    image?: string;
    // Optional solid color for this layer (usually only the base layer needs one).
    color?: string;
    // background-size — the tile the image repeats at, e.g. "200px 200px".
    size?: string;
    // background-position (starting offset).
    position?: string;
    // background-repeat (defaults to 'repeat' so the tile fills the viewport).
    repeat?: string;
    opacity?: number;
    // mix-blend-mode for layering (e.g. 'screen' to add light on light).
    blendMode?: string;
    // The layer's animation. Provide raw `keyframes` (the frames body) for full control, or the
    // convenience `from`/`to` on `property` (default 'transform'). Different `duration`s per layer
    // give the parallax depth. Omit to leave the layer static.
    animation?: {
        keyframes?: string;
        from?: string;
        to?: string;
        property?: string;
        duration?: string;
        timingFunction?: string;
        delay?: string;
        iterationCount?: string;
        direction?: string;
    };
}

// The animated main-body background (styleConfig.pageBackground). Absent/empty layers = feature off.
export interface IPageBackgroundConfig {
    // Base color painted behind all layers.
    color?: string;
    layers?: IPageBackgroundLayer[];
    // Optional: name of a URL query param whose numeric value scales the animation speed. When the
    // page URL carries `?<speedParam>=N`, every layer's animation duration is divided by N (higher N
    // = faster). Absent param, or N<=0, leaves the configured speeds untouched. The param name lives
    // here in config (generic in code); a storefront can wire it to a themed control (e.g. a "warp"
    // menu that links to ?warp=1..9).
    speedParam?: string;
}

export interface IGlobalStyleConfig {
    spinner?: ISpinnerConfig;
    // Animated effect applied ONLY to the storefront's main body background (see Layout).
    pageBackground?: IPageBackgroundConfig;
    // The styleConfig field also carries page-level style keys the Home editor owns; we don't
    // read those here, but keep the passthrough so JSON.parse round-trips them untouched.
    [prop: string]: unknown;
}

// Reject any config value that could break out of the scoped <style> we build, or pull in an
// external/active resource. Everything a starfield needs (gradients, sizes, transforms, %s) passes;
// angle brackets, url(), @import, javascript:, expression() and backslashes do not. Returns the
// value when safe, else undefined (so the declaration is simply skipped).
const safeCss = (v: unknown): string | undefined => {
    if (v == null) return undefined;
    const s = String(v);
    if (/[<>]/.test(s) || /url\s*\(/i.test(s) || /@import/i.test(s) || /javascript:/i.test(s) || /expression\s*\(/i.test(s) || s.includes('\\')) {
        return undefined;
    }
    return s;
};

export function pageBackgroundActive(cfg?: IPageBackgroundConfig): boolean {
    return !!(cfg && Array.isArray(cfg.layers) && cfg.layers.length > 0);
}

// Divide a CSS time value ("120s" / "800ms") by a speed factor, preserving the unit. Factors of 1
// (or unparseable durations) pass through unchanged.
function scaleDuration(dur: string, factor: number): string {
    if (factor === 1) return dur;
    const m = /^\s*([\d.]+)(ms|s)\s*$/.exec(dur);
    if (!m) return dur;
    return `${parseFloat(m[1]) / factor}${m[2]}`;
}

// Build the CSS text for the scoped <style> that paints the fixed backdrop (.sf-page-bg) and its
// per-layer backgrounds + @keyframes. All values are sanitized; returns '' when nothing is active.
// `speedFactor` (from the config's speedParam URL param) divides every animation duration.
export function pageBackgroundCss(cfg?: IPageBackgroundConfig, speedFactor = 1): string {
    if (!pageBackgroundActive(cfg)) return '';
    const rules: string[] = [];
    // The app is wrapped in a Fluent <FluentProvider> that paints an opaque surface background. It
    // sits above our z-index:-1 backdrop and would hide it (leaving the page looking blank/white), so
    // make the top-level provider transparent. Scoped to the root provider only — nested providers
    // inside popovers/dialogs keep their own surface.
    rules.push('#root > .fui-FluentProvider { background-color: transparent !important; }');
    const baseColor = safeCss(cfg!.color);
    if (baseColor) rules.push(`.sf-page-bg { background-color: ${baseColor}; }`);
    cfg!.layers!.forEach((layer, i) => {
        const decls: string[] = [];
        const image = safeCss(layer.image);
        const color = safeCss(layer.color);
        const size = safeCss(layer.size);
        const position = safeCss(layer.position);
        const repeat = safeCss(layer.repeat);
        const blend = safeCss(layer.blendMode);
        if (image) decls.push(`background-image: ${image};`);
        if (color) decls.push(`background-color: ${color};`);
        if (size) decls.push(`background-size: ${size};`);
        if (position) decls.push(`background-position: ${position};`);
        decls.push(`background-repeat: ${repeat || 'repeat'};`);
        if (typeof layer.opacity === 'number') decls.push(`opacity: ${layer.opacity};`);
        if (blend) decls.push(`mix-blend-mode: ${blend};`);
        const a = layer.animation;
        if (a) {
            const name = `sf-bg-${i}`;
            const duration = scaleDuration(safeCss(a.duration) || '60s', speedFactor);
            const timing = safeCss(a.timingFunction) || 'linear';
            const delay = safeCss(a.delay) || '0s';
            const iter = safeCss(a.iterationCount) || 'infinite';
            const direction = safeCss(a.direction) || 'normal';
            decls.push(`animation: ${name} ${duration} ${timing} ${delay} ${iter} ${direction};`);
            decls.push('will-change: transform, background-position;');
            let frames = safeCss(a.keyframes);
            if (!frames) {
                const prop = safeCss(a.property) || 'transform';
                const from = safeCss(a.from);
                const to = safeCss(a.to);
                if (from != null && to != null) frames = `from { ${prop}: ${from}; } to { ${prop}: ${to}; }`;
            }
            if (frames) rules.push(`@keyframes ${name} { ${frames} }`);
        }
        rules.push(`.sf-page-bg-layer-${i} { ${decls.join(' ')} }`);
    });
    return rules.join('\n');
}

export function parseGlobalStyleConfig(raw?: string): IGlobalStyleConfig {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw) as IGlobalStyleConfig;
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

// Build the CSS text for a scoped <style> that colors every Fluent spinner on the page plus
// the spinner label. Returns '' when nothing is configured so the caller can skip injection.
export function spinnerStyleCss(spinner?: ISpinnerConfig): string {
    if (!spinner) return '';
    const rules: string[] = [];
    if (spinner.color) {
        // Fluent v9 renders the spinner as an SVG <circle>; the visible arc is the `__tail`,
        // the faint track is the `__track`. Force the tail to the configured color.
        rules.push(`.fui-Spinner__spinnerTail { stroke: ${spinner.color} !important; }`);
        rules.push(`.fui-Spinner__spinner { color: ${spinner.color} !important; }`);
    }
    const labelDecls: string[] = [];
    if (spinner.labelColor) labelDecls.push(`color: ${spinner.labelColor} !important;`);
    if (spinner.labelFont) labelDecls.push(`font-family: ${spinner.labelFont} !important;`);
    if (labelDecls.length) {
        rules.push(`.spinner-label, .fui-Spinner__label { ${labelDecls.join(' ')} }`);
    }
    return rules.join('\n');
}
